import { useRef, useEffect, useState } from 'react';
import { listings } from '../../../mocks/listings';

const statusColors: Record<string, string> = {
  'For Sale': 'bg-emerald-50 text-emerald-700',
  'Under Contract': 'bg-amber-50 text-amber-700',
  'Sold': 'bg-[#F0EBE0] text-[#888]',
};

export default function ListingsSection() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [filter, setFilter] = useState<'All' | 'For Sale' | 'Under Contract' | 'Sold'>('All');

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const filtered = filter === 'All' ? listings : listings.filter((l) => l.status === filter);

  return (
    <section id="listings" className="py-24 bg-[#F8F6F1]">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto px-6 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-[#B8963E] text-xs font-bold tracking-widest uppercase mb-3">Properties · 매물</p>
            <h2 className="font-['Playfair_Display'] text-[#1A1A1A] text-3xl lg:text-4xl font-bold">
              Featured Listings
            </h2>
          </div>
          {/* Filter Tabs */}
          <div className="flex items-center bg-white rounded-full p-1 gap-1">
            {(['All', 'For Sale', 'Under Contract', 'Sold'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setFilter(tab)}
                className={`px-4 py-2 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  filter === tab
                    ? 'bg-[#B8963E] text-white'
                    : 'text-[#666] hover:text-[#1A1A1A]'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>

        {/* Listings Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((listing) => (
            <div key={listing.id} className="bg-white rounded-lg overflow-hidden group cursor-pointer">
              {/* Image */}
              <div className="relative overflow-hidden h-52">
                <img
                  src={listing.image}
                  alt={`${listing.address} - ${listing.city} GA real estate`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 left-3">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full ${statusColors[listing.status]}`}>
                    {listing.status}
                  </span>
                </div>
                <div className="absolute top-3 right-3 bg-white/90 text-[#1A1A1A] text-xs font-semibold px-2 py-1 rounded">
                  {listing.type}
                </div>
              </div>

              {/* Details */}
              <div className="p-5">
                <div className="font-['Playfair_Display'] text-[#1A1A1A] text-xl font-bold mb-1">
                  ${listing.price.toLocaleString()}
                </div>
                <div className="text-[#555] text-sm mb-3">
                  {listing.address}, {listing.city}, {listing.state} {listing.zip}
                </div>
                <p className="text-[#888] text-xs leading-relaxed mb-4 line-clamp-2">
                  {listing.description}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 pt-4 border-t border-[#F0EBE0]">
                  {listing.beds > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-[#555]">
                      <i className="ri-hotel-bed-fill text-[#B8963E]"></i>
                      <span className="font-medium">{listing.beds}</span> Beds
                    </div>
                  )}
                  {listing.baths > 0 && (
                    <div className="flex items-center gap-1.5 text-xs text-[#555]">
                      <i className="ri-water-percent-fill text-[#B8963E]"></i>
                      <span className="font-medium">{listing.baths}</span> Baths
                    </div>
                  )}
                  <div className="flex items-center gap-1.5 text-xs text-[#555]">
                    <i className="ri-ruler-2-fill text-[#B8963E]"></i>
                    <span className="font-medium">{listing.sqft.toLocaleString()}</span> sqft
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' }); }}
            className="inline-flex items-center gap-2 border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-8 py-3 rounded-md text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-search-line"></i>
            Request More Listings
          </a>
        </div>
      </div>
    </section>
  );
}
